'use client';

import { useTranslations } from '@/app/providers';
import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';

interface LogoConfig {
  file: string;
  maxHeight?: number;
}

// Customer logos list with optional custom sizing
const customerLogos: LogoConfig[] = [
  { file: 'red_software.svg', maxHeight: 50 }, // Logo più alto, ridotto per simmetria
  { file: 'condense.svg' },
  { file: 'devpunks.svg' },
  { file: 'mom.svg' },
  { file: 'toduba.svg' },
  { file: 'my_animal.svg' },
];

const DEFAULT_MAX_HEIGHT = 100;

const Customers = () => {
  const t = useTranslations();
  const [logos, setLogos] = useState<LogoConfig[]>([]);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [opacities, setOpacities] = useState<number[]>([]);

  useEffect(() => {
    setLogos(customerLogos);
  }, []);

  useEffect(() => {
    if (!carouselRef.current || logos.length === 0) return;

    const updateOpacities = () => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      const container = carousel.parentElement;
      if (!container) return;

      const containerCenter = container.offsetWidth / 2;

      const newOpacities: number[] = [];
      const duplicatedLogos = [...logos, ...logos, ...logos];

      duplicatedLogos.forEach((_, index) => {
        const logoElement = carousel.children[index] as HTMLElement;
        if (!logoElement) {
          newOpacities.push(0.2);
          return;
        }

        const logoRect = logoElement.getBoundingClientRect();
        const logoCenter = logoRect.left + logoRect.width / 2;
        const distance = Math.abs(logoCenter - containerCenter);
        const maxDistance = container.offsetWidth / 2;

        // Opacity: center = 1, decreases linearly towards outside (min 0.2)
        const opacity = Math.max(0.2, 1 - (distance / maxDistance) * 0.8);
        newOpacities.push(opacity);
      });

      setOpacities(newOpacities);
    };

    updateOpacities();

    const interval = setInterval(updateOpacities, 50);
    return () => clearInterval(interval);
  }, [logos]);

  if (logos.length === 0) {
    return (
      <section id='clienti' className='container mx-auto px-6 py-12'>
        <h2 className='text-2xl font-semibold text-center mb-8 text-white'>
          {t.customers.title}
        </h2>
      </section>
    );
  }

  // Duplicate logos to create smooth infinite loop
  const duplicatedLogos = [...logos, ...logos, ...logos];

  return (
    <section
      id='clienti'
      className='container mx-auto px-6 py-12 mt-16 md:mt-0'
    >
      <h2 className='text-2xl font-semibold text-center mb-12 text-white'>
        {t.customers.title}
      </h2>
      <div className='relative w-full h-32 md:h-40 flex items-center justify-center overflow-hidden'>
        <div
          ref={carouselRef}
          className='customers-carousel flex items-center gap-6 md:gap-8'
          style={{ '--logo-count': logos.length } as React.CSSProperties}
        >
          {duplicatedLogos.map((logo, index) => {
            const opacity = opacities[index] ?? 0.2;
            const maxHeight = logo.maxHeight ?? DEFAULT_MAX_HEIGHT;
            return (
              <div
                key={`${logo.file}-${index}`}
                className='customer-logo shrink-0 w-[230px] h-[127px] flex items-center justify-center transition-opacity duration-300 p-4'
                style={{ opacity }}
              >
                <Image
                  src={`/customers/${logo.file}`}
                  alt={`Logo ${logo.file
                    .replace('.svg', '')
                    .replace('_', ' ')}`}
                  width={200}
                  height={maxHeight}
                  className='object-contain w-auto h-auto'
                  style={{ maxWidth: 200, maxHeight }}
                  unoptimized
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Customers;
